/**
 * Loan Application Form with multi-step process
 */
import * as React from "react";
import { useState } from "react";
import { submitLoanApplicationViaEmail } from "../utils/emailService";
import SuccessModal from "./SuccessModal";
import { renderFormStep } from "./FormRenderer";

function ContactForm() {
  // Define initial form data state to use for resets
  const initialFormData = {
    // Path Selection
    applicantType: "",
    
    // Common Fields
    fullName: "",
    email: "",
    phoneNumber: "",
    address: "",
    businessName: "", // Company name for business owners or employer name for individuals
    loanAmount: "",
    loanPurpose: "",
    otherPurpose: "",
    repaymentDuration: "",
    yearsInOperation: "", // Employment duration for individuals, business existence for business owners
    monthlyIncome: "", // Income for individuals, revenue for business
    
    // Personal Details (Employed Individual)
    dateOfBirth: "",
    employmentCategory: "", // IPPIS, LAGOS STATE, or Others
    
    // Business Details (Business Owner)
    businessNature: "",
    otherBusinessNature: "", // If businessNature is "Other"
    hasInvoices: "", // Only for business owners
    
    // Common Additional Information
    existingLoans: "",
    servicing: "",
    totalBorrowing: "",
    borrowingDate: "",
    currentExposure: "",
    lendingInstitutions: "",
    bvn: "",
    preferredContactMethod: "",
    consent: false
  };

  const [currentStep, setCurrentStep] = useState(1);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [formData, setFormData] = useState(initialFormData);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value
    });
    
    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ""
      });
    }
  };

  const validateStep = (step) => {
    let isValid = true;
    let newErrors = {};
    
    // Step 1 now combines the applicant type and the personal/business details
    if (step === 1) {
      // Always validate applicant type
      if (!formData.applicantType) {
        newErrors.applicantType = "Please select an applicant type";
      }
      
      // If applicant type is selected, also validate the corresponding fields
      if (formData.applicantType === "Employed Individual") {
        // Personal Details validation
        if (!formData.fullName) newErrors.fullName = "Please enter your full name";
        if (!formData.email) newErrors.email = "Please enter your email address";
        if (!formData.phoneNumber) newErrors.phoneNumber = "Please enter your phone number";
        if (!formData.address) newErrors.address = "Please enter your home address";
        if (!formData.dateOfBirth) newErrors.dateOfBirth = "Please enter your date of birth";
        if (!formData.employmentCategory) newErrors.employmentCategory = "Please select your employment category";
        if (!formData.businessName) newErrors.businessName = "Please enter your employer's name";
        if (!formData.yearsInOperation) newErrors.yearsInOperation = "Please select your employment duration";
        if (!formData.monthlyIncome) newErrors.monthlyIncome = "Please select your monthly income range";
      } 
      else if (formData.applicantType === "Business Owner") {
        // Business Profile validation
        if (!formData.fullName) newErrors.fullName = "Please enter the contact person's name";
        if (!formData.businessName) newErrors.businessName = "Please enter your business name";
        if (!formData.email) newErrors.email = "Please enter your business email";
        if (!formData.phoneNumber) newErrors.phoneNumber = "Please enter your business phone number";
        if (!formData.address) newErrors.address = "Please enter your business address";
        if (!formData.businessNature) newErrors.businessNature = "Please select the nature of your business";
        if (formData.businessNature === "Other" && !formData.otherBusinessNature) {
          newErrors.otherBusinessNature = "Please specify the nature of your business";
        }
        if (!formData.yearsInOperation) newErrors.yearsInOperation = "Please select how long your business has been operating";
        if (!formData.monthlyIncome) newErrors.monthlyIncome = "Please select your monthly revenue range";
      }
    } 
    // For steps 2-3, validation depends on the selected applicant type
    else if (formData.applicantType === "Employed Individual") {
      // Employed Individual path
      if (step === 2) {
        // Now step 2 is Loan Details validation (previously step 3)
        if (!formData.loanAmount) newErrors.loanAmount = "Please enter the loan amount";
        if (!formData.loanPurpose) newErrors.loanPurpose = "Please select the loan purpose";
        if (formData.loanPurpose === "Other" && !formData.otherPurpose) {
          newErrors.otherPurpose = "Please specify the loan purpose";
        }
        if (!formData.repaymentDuration) newErrors.repaymentDuration = "Please select your preferred repayment duration";
        if (!formData.existingLoans) newErrors.existingLoans = "Please specify if you have existing loans";
        if (formData.existingLoans === "Yes" && !formData.servicing) {
          newErrors.servicing = "Please specify if you are currently servicing your loans";
        }
      }
      else if (step === 3) {
        // Now step 3 is Additional Information validation (previously step 4)
        if (formData.existingLoans === "Yes") {
          if (!formData.totalBorrowing) newErrors.totalBorrowing = "Please enter your total borrowing exposure";
          if (!formData.borrowingDate) newErrors.borrowingDate = "Please enter the date of borrowing";
          if (!formData.currentExposure) newErrors.currentExposure = "Please enter your current exposure";
          if (!formData.lendingInstitutions) newErrors.lendingInstitutions = "Please enter the names of lending institutions";
          if (!formData.bvn) newErrors.bvn = "Please enter your BVN";
        }
        if (!formData.preferredContactMethod) newErrors.preferredContactMethod = "Please select your preferred contact method";
        if (!formData.consent) newErrors.consent = "You must consent to proceed";
      }
    } 
    // Business Owner path
    else if (formData.applicantType === "Business Owner") {
      if (step === 2) {
        // Now step 2 is Business Loan Requirements validation (previously step 3)
        if (!formData.loanAmount) newErrors.loanAmount = "Please enter the loan amount required";
        if (!formData.loanPurpose) newErrors.loanPurpose = "Please select the use of funds";
        if (!formData.repaymentDuration) newErrors.repaymentDuration = "Please select your preferred repayment duration";
        if (!formData.existingLoans) newErrors.existingLoans = "Please specify if you have existing loans or overdrafts";
        if (formData.existingLoans === "Yes" && !formData.servicing) {
          newErrors.servicing = "Please specify if you are currently servicing these loans";
        }
      }
      else if (step === 3) {
        // Now step 3 is Business Additional Information validation (previously step 4)
        if (formData.existingLoans === "Yes") {
          if (!formData.totalBorrowing) newErrors.totalBorrowing = "Please enter your total borrowing exposure";
          if (!formData.borrowingDate) newErrors.borrowingDate = "Please enter the date of borrowing";
          if (!formData.currentExposure) newErrors.currentExposure = "Please enter your current exposure";
          if (!formData.lendingInstitutions) newErrors.lendingInstitutions = "Please enter the names of lending institutions";
          if (!formData.bvn) newErrors.bvn = "Please enter the Director's BVN";
        }
        if (!formData.hasInvoices) newErrors.hasInvoices = "Please specify if you have invoices or contracts with reputable firms";
        if (!formData.preferredContactMethod) newErrors.preferredContactMethod = "Please select your preferred contact method";
        if (!formData.consent) newErrors.consent = "You must consent to proceed";
      }
    }
    
    // Update errors state
    setErrors(newErrors);
    
    // Check if there are any errors
    if (Object.keys(newErrors).length > 0) {
      isValid = false;
    }
    
    return isValid;
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    setCurrentStep(currentStep - 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate all steps before submitting
    let isFormValid = true;
    for (let i = 1; i <= 3; i++) { // Now we only have 3 steps total
      if (!validateStep(i)) {
        setCurrentStep(i); // Move to the step that has validation errors
        isFormValid = false;
        break;
      }
    }
    
    if (isFormValid) {
      try {
        setIsSubmitting(true);
        
        // Call the email submission service
        const response = await submitLoanApplicationViaEmail(formData);
        console.log("Submission response:", formData);
        
        if (response.success) {
          setShowSuccessModal(true);
        } else {
          // Handle error response
          alert("Error submitting application. Please try again.");
        }
      } catch (error) {
        console.error("Submission error:", error);
        alert("Error submitting application. Please try again.");
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  return (
    <form className="loan-application-form" onSubmit={handleSubmit}>
      {/* Success Modal */}
      <SuccessModal 
        visible={showSuccessModal} 
        onClose={(resetForm) => {
          setShowSuccessModal(false);
          if (resetForm) {
            // Reset form data and go back to step 1
            setFormData(initialFormData);
            setCurrentStep(1);
            setErrors({});
          }
        }} 
      />
      
      <div className="form-header">
        <h2 className="form-title">Orbit Financial Services – Loan Request Form</h2>
        <p className="required-notice">All fields marked with <span className="required">*</span> are required</p>
        <div className="progress-container">
          <div className="progress-indicator">
            <div className="step-container">
              <div className={`step ${currentStep >= 1 ? "active" : ""}`}>1</div>
              <div className="step-label">Application & Profile</div>
            </div>
            <div className="progress-line"></div>
            <div className="step-container">
              <div className={`step ${currentStep >= 2 ? "active" : ""}`}>2</div>
              <div className="step-label">Loan Details</div>
            </div>
            <div className="progress-line"></div>
            <div className="step-container">
              <div className={`step ${currentStep >= 3 ? "active" : ""}`}>3</div>
              <div className="step-label">Additional Information</div>
            </div>
          </div>
        </div>
        {formData.applicantType && (
          <div className="selected-path">
            <span className="path-label">Application Type:</span>
            <span className="path-value">{formData.applicantType}</span>
          </div>
        )}
      </div>

      {/* Render form step based on current step */}
      {renderFormStep(
        currentStep, 
        formData, 
        handleInputChange, 
        errors, 
        nextStep, 
        prevStep,
        isSubmitting
      )}
          
      <style>{`
        .loan-application-form {
          display: flex;
          flex-direction: column;
          gap: 31px;
          width: 80%;
          max-width: 900px;
          margin: 0 auto;
          background-color: rgba(0, 50, 126, 0.9);
          padding: 40px;
          border-radius: 10px;
          box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
        }
        
        .form-header {
          text-align: center;
          margin-bottom: 30px;
        }
        
        .form-title {
          color: white;
          font-size: 24px;
          margin-bottom: 20px;
        }
        
        .required-notice {
          color: white;
        }
        
        .step-title {
          color: white;
          font-size: 20px;
          margin-bottom: 20px;
          border-bottom: 1px solid rgba(255, 255, 255, 0.2);
          padding-bottom: 10px;
        }
        
        .progress-container {
          margin: 20px 0;
        }
        
        .progress-indicator {
          display: flex;
          justify-content: center;
          align-items: center;
          flex-wrap: nowrap;
          gap: 10px;
        }
        
        .step-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          width: 120px;
        }
        
        .step {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          background-color: rgba(255, 255, 255, 0.3);
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: bold;
          flex-shrink: 0;
          margin-bottom: 8px;
        }
        
        .step.active {
          background-color: var(--Orbit-Color, #78d3ce);
        }
        
        .progress-line {
          flex-grow: 1;
          height: 3px;
          background-color: rgba(255, 255, 255, 0.3);
          max-width: 100px;
          margin-top: 20px;
        }
        
        .step-label {
          color: white;
          font-size: 13px;
          text-align: center;
          width: 100%;
          opacity: 0.8;
        }
        
        .form-step {
          display: flex;
          flex-direction: column;
          gap: 20px;
        }
        
        .combined-step {
          gap: 10px;
        }
        
        .conditional-step-content {
          margin-top: 20px;
          padding-top: 20px;
          border-top: 1px dashed rgba(255, 255, 255, 0.2);
          animation: fadeIn 0.5s;
        }
        
        .step-subtitle {
          color: var(--Orbit-Color, #78d3ce);
          font-size: 18px;
          margin-bottom: 20px;
        }
        
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        .form-group {
          display: flex;
          flex-direction: column;
          gap: 8px;
          width: 100%;
        }
        
        .form-label {
          color: rgba(255, 255, 255, 1);
          font-weight: 500;
        }
        
        .required {
          color: #ff6b6b;
          font-weight: bold;
          margin-left: 4px;
        }
        
        .error-message {
          color: #ff6b6b;
          font-size: 14px;
          margin-top: 4px;
        }
        
        .input-error {
          border: 1px solid #ff6b6b !important;
        }
        
        .form-input,
        .form-textarea,
        select.form-input {
          border-radius: 6px;
          border: 1px solid #b0b0b0;
          background: var(--Shade-White, #fff);
          color: var(--Orbit-Color-Style-Slate, #475569);
          padding: 12px;
          font-family: Inter, sans-serif;
          height: 50px;
          width: 100%;
          box-sizing: border-box;
          font-size: 16px;
          -webkit-appearance: none;
          -moz-appearance: none;
          appearance: none;
        }
        
        select.form-input {
          background-image: url("data:image/svg+xml,%3Csvg width='10' height='6' viewBox='0 0 10 6' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1L5 5L9 1' stroke='%23475569' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
          background-repeat: no-repeat;
          background-position: right 12px center;
          padding-right: 35px;
        }
        
        .form-textarea {
          height: 120px;
          padding-top: 8px;
          resize: vertical;
          min-height: 120px;
        }
        
        .radio-group,
        .checkbox-group {
          margin: 10px 0;
          width: 100%;
        }
        
        .radio-options {
          display: flex;
          flex-wrap: wrap;
          gap: 15px;
          margin-top: 10px;
          width: 100%;
        }
        
        .radio-options.vertical {
          flex-direction: column;
          gap: 10px;
        }
        
        .radio-option,
        .checkbox-option {
          display: flex;
          align-items: center;
          gap: 8px;
          color: white;
          min-height: 44px;
          padding: 4px 0;
        }
        
        .radio-option label,
        .checkbox-option label {
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 8px;
        }
        
        input[type="radio"],
        input[type="checkbox"] {
          accent-color: var(--Orbit-Color, #78d3ce);
          width: 20px;
          height: 20px;
          cursor: pointer;
          margin: 0;
        }
        
        .form-buttons {
          display: flex;
          justify-content: space-between;
          margin-top: 20px;
          gap: 15px;
        }
        
        .primary-button,
        .secondary-button {
          padding: 14px 24px;
          border-radius: 6px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-feature-settings: "cv01" on, "cv03" on, "cv04" on;
          cursor: pointer;
          font-weight: 500;
          min-width: 120px;
          border: none;
          font-size: 16px;
          transition: opacity 0.2s ease;
        }
        
        .primary-button:hover,
        .secondary-button:hover {
          opacity: 0.9;
        }
        
        .primary-button {
          background: var(--Orbit-Color, #78d3ce);
          color: white;
        }
        
        .secondary-button {
          background: transparent;
          border: 1px solid white;
          color: white;
        }
        
        .next-button {
          margin-left: auto;
        }
        
        .consent-group {
          margin-top: 20px;
        }
        
        .checkbox-container {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          cursor: pointer;
        }
        
        .consent-text {
          flex: 1;
          line-height: 1.4;
        }
        
        /* Mobile Styles */
        @media (max-width: 991px) {
          .loan-application-form {
            width: 100%;
            padding: 20px;
            border-radius: 0;
            margin: 0;
            gap: 20px;
          }
          
          .form-title {
            font-size: 20px;
            margin-bottom: 15px;
          }
          
          .step-title {
            font-size: 18px;
          }
          
          .progress-container {
            margin: 15px 0;
          }
          
          .progress-indicator {
            padding: 0;
            gap: 5px;
          }
          
          .step-container {
            width: 90px;
          }
          
          .step {
            width: 32px;
            height: 32px;
            font-size: 14px;
            margin-bottom: 6px;
          }
          
          .progress-line {
            max-width: 40px;
            margin-top: 16px;
          }
          
          .step-label {
            font-size: 11px;
            line-height: 1.2;
          }
          
          .conditional-step-content {
            margin-top: 15px;
            padding-top: 15px;
          }
          
          .step-subtitle {
            font-size: 16px;
            margin-bottom: 15px;
          }
          
          .form-step {
            gap: 16px;
          }
          
          .form-group {
            gap: 6px;
          }
          
          .form-input,
          .form-textarea,
          select.form-input {
            height: 44px;
            padding: 10px;
            font-size: 15px;
          }
          
          .form-textarea {
            min-height: 100px;
          }
          
          .radio-options {
            gap: 10px;
          }
          
          .radio-option,
          .checkbox-option {
            min-height: 40px;
          }
          
          .form-buttons {
            flex-direction: column-reverse;
            gap: 12px;
            margin-top: 24px;
          }
          
          .primary-button,
          .secondary-button {
            width: 100%;
            height: 44px;
            padding: 0;
            font-size: 15px;
          }
          
          .next-button {
            margin-left: 0;
          }
          
          .consent-group {
            margin-top: 16px;
          }
             .consent-text {
          font-size: 14px;
        }
        
        input[type="radio"],
        input[type="checkbox"] {
          width: 18px;
          height: 18px;
        }
        
        .selected-path {
          display: flex;
          align-items: center;
          background-color: rgba(120, 211, 206, 0.2);
          padding: 8px 12px;
          border-radius: 6px;
          margin-top: 15px;
          justify-content: center;
        }
        
        .path-label {
          font-weight: 600;
          color: #ffffff;
          margin-right: 6px;
        }
        
        .path-value {
          color: #ffffff;
          font-weight: 600;
        }
        
        .error-message-box {
          background-color: rgba(255, 107, 107, 0.1);
          border-left: 4px solid #ff6b6b;
          padding: 12px;
          color: white;
          margin-bottom: 20px;
          border-radius: 0 6px 6px 0;
        }
        }
        
        /* Small Mobile Devices */
        @media (max-width: 360px) {
          .loan-application-form {
            padding: 15px;
          }
          
          .form-title {
            font-size: 18px;
          }
          
          .step-title {
            font-size: 16px;
          }
          
          .step-container {
            width: 80px;
          }
          
          .step-label {
            font-size: 10px;
          }
          
          .form-input,
          .form-textarea,
          select.form-input {
            font-size: 14px;
          }
        }
      `}</style>
    </form>
  );
}

export default ContactForm;
